import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRightLeft,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Copy,
  Download,
  ExternalLink,
  Import,
  Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { InvoiceFormModal } from '@/features/invoices/components/modals';
import { CreateInvoiceModal } from '@/features/invoices/components/modals';
import { getInvoice, updateInvoice, getInvoiceV4 } from '@/features/invoices/api';
import { transformV4InvoiceToV3 } from '@/features/invoices/types';
import type { Invoice, UpdateInvoiceInput } from '@/features/invoices/types';
import { getAccountInvoices } from '../api';
import {
  loadColumnFilters,
  saveColumnFilters,
} from '../lib/tableLocalStorage';
import { useColumnState } from '@/hooks/useColumnState';
import { AccountsColumnsMenu } from './AccountsColumnsMenu';
import { AccountsColumnResizeHandle } from './AccountsColumnResizeHandle';
import ReassignDialog from './ReassignDialog';
import ImportInvoicesModal from './ImportInvoicesModal';
import InvoiceDetailSheet from './InvoiceDetailSheet';
import type { InvoiceDetail, AccountDashboardData } from '../types';

// =============================================================================
// Column Definition
// =============================================================================

interface ColumnDef<T> {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: string;
  filterType?: 'text' | 'date-range' | 'select' | 'number-range';
  filterOptions?: { label: string; value: string }[];
  getValue: (item: T) => string | number | null;
  render?: (item: T, helpers: InvoiceRowHelpers) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  className?: string;
}

interface InvoiceRowHelpers {
  onEdit: (invoice: InvoiceDetail) => void;
  onReassign: (invoice: InvoiceDetail) => void;
}

const STATUS_OPTIONS = [
  { label: 'Preparation', value: 'preparation' },
  { label: 'Sent', value: 'sent' },
  { label: 'Paid', value: 'paid' },
  { label: 'Cancelled', value: 'cancelled' },
  { label: 'Void', value: 'void' },
];

const statusVariantMap: Record<string, 'secondary' | 'warning' | 'success' | 'outline' | 'destructive'> = {
  preparation: 'secondary',
  sent: 'warning',
  paid: 'success',
  cancelled: 'outline',
  void: 'destructive',
};

const COLUMNS: ColumnDef<InvoiceDetail>[] = [
  {
    key: 'invoiceNumber',
    label: 'Invoice #',
    sortKey: 'invoiceNumber',
    filterType: 'text',
    getValue: (inv) => inv.invoiceNumber,
    render: (inv) => <span className="font-medium">{inv.invoiceNumber}</span>,
  },
  {
    key: 'projectName',
    label: 'Project',
    sortKey: 'projectName',
    filterType: 'text',
    getValue: (inv) => inv.projectName || '',
    render: (inv) => {
      const name = inv.projectName || '';
      if (!name) return '—';
      // Wrap onto multiple lines instead of truncating so the full name is visible.
      return (
        <span className="block whitespace-normal break-words leading-snug">
          {name}
        </span>
      );
    },
  },
  {
    key: 'status',
    label: 'Status',
    filterType: 'select',
    filterOptions: STATUS_OPTIONS,
    getValue: (inv) => inv.status,
    render: (inv) => {
      const statusVariant = statusVariantMap[inv.status] || 'secondary';
      const isOverdue =
        inv.status === 'sent' && inv.dueDate && new Date(inv.dueDate) < new Date();
      return (
        <div className="flex items-center gap-2">
          <Badge variant={statusVariant}>{inv.status}</Badge>
          {isOverdue && <Badge variant="destructive">Overdue</Badge>}
        </div>
      );
    },
  },
  {
    key: 'invoiceDate',
    label: 'Issue Date',
    sortKey: 'invoiceDate',
    filterType: 'date-range',
    getValue: (inv) => inv.invoiceDate,
    render: (inv) => formatDate(inv.invoiceDate),
  },
  {
    key: 'dueDate',
    label: 'Due Date',
    sortKey: 'dueDate',
    filterType: 'date-range',
    getValue: (inv) => inv.dueDate || '',
    render: (inv) => {
      const isOverdue =
        inv.status === 'sent' && inv.dueDate && new Date(inv.dueDate) < new Date();
      return (
        <span className={isOverdue ? 'text-destructive font-medium' : ''}>
          {inv.dueDate ? formatDate(inv.dueDate) : '—'}
        </span>
      );
    },
  },
  {
    key: 'paymentDate',
    label: 'Paid Date',
    sortKey: 'paymentDate',
    filterType: 'date-range',
    getValue: (inv) => inv.paymentDate || '',
    render: (inv) => (inv.paymentDate ? formatDate(inv.paymentDate) : '—'),
  },
  {
    key: 'subtotal',
    label: 'Subtotal',
    sortKey: 'subtotal',
    defaultVisible: false,
    filterType: 'number-range',
    getValue: (inv) => parseFloat(inv.subtotal) || 0,
    render: (inv) => formatCurrency(parseFloat(inv.subtotal), inv.currency),
    align: 'right',
  },
  {
    key: 'taxAmount',
    label: 'Tax',
    sortKey: 'taxAmount',
    defaultVisible: false,
    filterType: 'number-range',
    getValue: (inv) => parseFloat(inv.taxAmount) || 0,
    render: (inv) => formatCurrency(parseFloat(inv.taxAmount), inv.currency),
    align: 'right',
  },
  {
    key: 'total',
    label: 'Total',
    sortKey: 'total',
    filterType: 'number-range',
    getValue: (inv) => parseFloat(inv.total) || 0,
    render: (inv) => (
      <span className="font-medium">{formatCurrency(parseFloat(inv.total), inv.currency)}</span>
    ),
    align: 'right',
  },
  {
    key: 'salesManagerName',
    label: 'Sales Manager',
    sortKey: 'salesManagerName',
    filterType: 'text',
    getValue: (inv) => inv.salesManagerName || '',
    render: (inv) => inv.salesManagerName || '—',
  },
  {
    key: 'contractualManagerName',
    label: 'Contractual Manager',
    sortKey: 'contractualManagerName',
    filterType: 'text',
    getValue: (inv) => inv.contractualManagerName || '',
    render: (inv) => inv.contractualManagerName || '—',
  },
  {
    key: 'poNumber',
    label: 'PO #',
    sortKey: 'poNumber',
    filterType: 'text',
    getValue: (inv) => inv.poNumber || '',
    render: (inv) => inv.poNumber || '—',
  },
  {
    key: 'gdriveLink',
    label: 'Drive',
    defaultVisible: false,
    noFilter: true,
    getValue: (inv) => inv.gdriveLink || '',
    render: (inv) =>
      inv.gdriveLink ? (
        <a
          href={inv.gdriveLink}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-atek-blue hover:underline"
          onClick={(e) => e.stopPropagation()}
          title="Open in Google Drive"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Drive
        </a>
      ) : (
        '—'
      ),
    align: 'center',
  },
  {
    key: 'hubspotLink',
    label: 'HubSpot',
    defaultVisible: false,
    noFilter: true,
    getValue: (inv) => inv.hubspotLink || '',
    render: (inv) =>
      inv.hubspotLink ? (
        <a
          href={inv.hubspotLink}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-atek-blue hover:underline"
          onClick={(e) => e.stopPropagation()}
          title="Open in HubSpot"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          HubSpot
        </a>
      ) : (
        '—'
      ),
    align: 'center',
  },
  {
    key: 'quickbooksLink',
    label: 'QuickBooks',
    defaultVisible: false,
    noFilter: true,
    getValue: (inv) => inv.quickbooksLink || '',
    render: (inv) =>
      inv.quickbooksLink ? (
        <a
          href={inv.quickbooksLink}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-atek-blue hover:underline"
          onClick={(e) => e.stopPropagation()}
          title="Open in QuickBooks"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          QBO
        </a>
      ) : (
        '—'
      ),
    align: 'center',
  },
  {
    key: 'actions',
    label: '',
    noFilter: true,
    getValue: () => '',
    render: (inv, { onEdit, onReassign }) => (
      <div className="flex items-center gap-2">
        {inv.mongoInvoiceId && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(inv);
            }}
            className="text-atek-blue hover:underline text-sm cursor-pointer"
          >
            Edit
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onReassign(inv);
          }}
          className="text-muted-foreground hover:text-foreground cursor-pointer"
          title="Reassign to another account"
        >
          <ArrowRightLeft className="h-4 w-4" />
        </button>
      </div>
    ),
    align: 'right',
  },
];

// =============================================================================
// localStorage Persistence
// =============================================================================

// `-v2` suffix on visibility/order/widths: the previous InvoicesTab persisted
// only visibility under `atek-account-invoices-column-visibility`. We now
// persist three pieces of state via `useColumnState`; bumping the key avoids
// inheriting a stale visibility map from before the new Project / Manager /
// PO / Drive / HubSpot / QBO columns existed (which would default-hide them
// for current users).
const VISIBILITY_STORAGE_KEY = 'atek-account-invoices-column-visibility-v2';
const ORDER_STORAGE_KEY = 'atek-account-invoices-column-order-v2';
const WIDTHS_STORAGE_KEY = 'atek-account-invoices-column-widths-v2';
const FILTERS_STORAGE_KEY_PREFIX = 'atek-account-invoices-column-filters';
const DEFAULT_COLUMN_WIDTH = 160;


// =============================================================================
// CSV Export Utilities
// =============================================================================

function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function exportToCSV(items: InvoiceDetail[], columns: ColumnDef<InvoiceDetail>[], filename: string) {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => escapeCSVValue(c.label)).join(',');
  const rows = items.map((item) =>
    exportCols.map((c) => escapeCSVValue(c.getValue(item))).join(','),
  );
  const csvContent = [header, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function copyToClipboard(items: InvoiceDetail[], columns: ColumnDef<InvoiceDetail>[]): Promise<void> {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => c.label).join('\t');
  const rows = items.map((item) =>
    exportCols.map((c) => String(c.getValue(item) ?? '')).join('\t'),
  );
  const text = [header, ...rows].join('\n');
  return navigator.clipboard.writeText(text);
}

// =============================================================================
// SortableHeader Component
// =============================================================================

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSortKey?: string;
  sortDirection?: 1 | -1;
  onSort: (key: string) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// Filter Row Renderer
// =============================================================================

function renderColumnFilter(
  col: ColumnDef<InvoiceDetail>,
  columnFilters: Record<string, string>,
  setColumnFilter: (key: string, value: string) => void,
) {
  if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

  if (col.filterType === 'select' && col.filterOptions) {
    const selected: string[] = (() => {
      try { return columnFilters[col.key] ? JSON.parse(columnFilters[col.key]) : []; }
      catch { return []; }
    })();

    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((o) => o !== opt)
        : [...selected, opt];
      setColumnFilter(col.key, next.length ? JSON.stringify(next) : '');
    };

    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-start font-normal">
              {selected.length ? `${selected.length} selected` : '—'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {col.filterOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
                onSelect={(e) => e.preventDefault()}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  if (col.filterType === 'date-range') {
    const raw = columnFilters[col.key] || '';
    const [from = '', to = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={from}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${to}`)}
            title="From"
          />
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={to}
            onChange={(e) => setColumnFilter(col.key, `${from}>>${e.target.value}`)}
            title="To"
          />
        </div>
      </TableHead>
    );
  }

  if (col.filterType === 'number-range') {
    const raw = columnFilters[col.key] || '';
    const [min = '', max = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={min}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${max}`)}
            placeholder="Min"
          />
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={max}
            onChange={(e) => setColumnFilter(col.key, `${min}>>${e.target.value}`)}
            placeholder="Max"
          />
        </div>
      </TableHead>
    );
  }

  return (
    <TableHead key={`filter-${col.key}`} className="p-1">
      <Input
        className="h-7 text-xs"
        value={columnFilters[col.key] || ''}
        onChange={(e) => setColumnFilter(col.key, e.target.value)}
        placeholder="..."
      />
    </TableHead>
  );
}

// =============================================================================
// InvoicesTab Component (Mode B: server-side pagination + client-side refinement)
// =============================================================================

interface InvoicesTabProps {
  accountId: string;
}

// Mirror SensorsTab / QuotesTab: fetch everything in one page so users scroll
// + filter client-side, no pagination footer.
const PAGE_SIZE = 5000;

export default function InvoicesTab({ accountId }: InvoicesTabProps) {
  const queryClient = useQueryClient();

  // Server-side state
  const [page, setPage] = useState(0);

  const [isExportingAll, setIsExportingAll] = useState(false);

  // Client-side column filters (refinement within page)
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    () => loadColumnFilters(FILTERS_STORAGE_KEY_PREFIX, accountId),
  );

  // Visibility / order / widths managed by the shared hook (src/hooks/useColumnState.ts)
  // so InvoicesTab gets the same drag-to-reorder + drag-to-resize behaviour as
  // QuotesTab / SensorsTab / TransmittersTab.
  const {
    orderedColumns,
    visibleColumns,
    columnWidths,
    isVisible,
    toggleColumn,
    setColumnOrder,
    setColumnWidth,
  } = useColumnState<ColumnDef<InvoiceDetail>>({
    columns: COLUMNS,
    storageKeys: {
      visibility: VISIBILITY_STORAGE_KEY,
      order: ORDER_STORAGE_KEY,
      widths: WIDTHS_STORAGE_KEY,
    },
  });

  const widthFor = (key: string) => columnWidths[key] ?? DEFAULT_COLUMN_WIDTH;
  const totalTableWidth = visibleColumns.reduce(
    (sum, c) => sum + widthFor(c.key),
    0,
  );

  // Client-side sort (within page)
  const [sortKey, setSortKey] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<{ mongoId?: string; pgId: string } | null>(null);
  const [detailInvoice, setDetailInvoice] = useState<InvoiceDetail | null>(null);
  const [reassignInvoice, setReassignInvoice] = useState<InvoiceDetail | null>(null);
  const [isImportOpen, setIsImportOpen] = useState(false);

  const dashboardData = queryClient.getQueryData<AccountDashboardData>(['account-dashboard', accountId]);
  const accountName = dashboardData?.account?.name || 'Current Account';
  const hasLegacyOrg = !!(dashboardData?.account?.mongo_organisation_id || dashboardData?.account?.organisation_id);

  // Persist column filters to localStorage
  useEffect(() => {
    saveColumnFilters(FILTERS_STORAGE_KEY_PREFIX, accountId, columnFilters);
  }, [columnFilters, accountId]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Extract text column filter values for server-side search
  const columnSearchText = useMemo(() => {
    return Object.entries(columnFilters)
      .filter(([, value]) => {
        if (!value) return false;
        if (value.includes('>>')) return false;
        if (value.startsWith('[')) return false;
        return true;
      })
      .map(([, value]) => value)
      .join(' ');
  }, [columnFilters]);

  const [debouncedColumnSearch, setDebouncedColumnSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedColumnSearch(columnSearchText);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [columnSearchText]);

  // Server-side data fetch
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['account-invoices', accountId, page, debouncedColumnSearch],
    queryFn: () =>
      getAccountInvoices(accountId, {
        pageNumber: page,
        pageLength: PAGE_SIZE,
        search: debouncedColumnSearch || undefined,
      }),
  });

  const invoices = data?.data || [];
  const total = data?.total || 0;

  // Client-side column filtering (AND-combined, within current page)
  const filteredItems = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
    if (activeFilters.length === 0) return invoices;

    return invoices.filter((item) =>
      activeFilters.every(([colKey, filterValue]) => {
        const col = COLUMNS.find((c) => c.key === colKey);
        if (!col || col.noFilter) return true;

        const rawValue = col.getValue(item);

        if (col.filterType === 'date-range') {
          const [from, to] = filterValue.split('>>');
          const dateStr = String(rawValue || '');
          if (from && dateStr < from) return false;
          if (to && dateStr > to + 'T23:59:59') return false;
          return true;
        }

        if (col.filterType === 'select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            return selected.length === 0 || selected.includes(String(rawValue || ''));
          } catch { return true; }
        }

        if (col.filterType === 'number-range') {
          const [minStr, maxStr] = filterValue.split('>>');
          const num = Number(rawValue);
          if (isNaN(num)) return false;
          if (minStr && num < Number(minStr)) return false;
          if (maxStr && num > Number(maxStr)) return false;
          return true;
        }

        return String(rawValue ?? '').toLowerCase().includes(filterValue.toLowerCase());
      }),
    );
  }, [invoices, columnFilters]);

  // Client-side sorting (within filtered page)
  const sortedItems = useMemo(() => {
    if (!sortKey) return filteredItems;
    const col = COLUMNS.find((c) => c.sortKey === sortKey);
    if (!col) return filteredItems;

    return [...filteredItems].sort((a, b) => {
      const aVal = col.getValue(a);
      const bVal = col.getValue(b);
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * sortDirection;
      }
      return String(aVal).localeCompare(String(bVal)) * sortDirection;
    });
  }, [filteredItems, sortKey, sortDirection]);

  const handleSort = (key: string) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 1 ? -1 : 1);
    } else {
      setSortKey(key);
      setSortDirection(1);
    }
  };

  // Fetch full invoice details when one is selected
  const { data: fullInvoice, isLoading: invoiceLoading } = useQuery({
    queryKey: ['invoice-detail', selectedInvoice?.mongoId || selectedInvoice?.pgId],
    queryFn: async (): Promise<Invoice> => {
      let invoice: Invoice;
      if (selectedInvoice?.mongoId) {
        invoice = await getInvoice(selectedInvoice.mongoId);
      } else {
        const v4 = await getInvoiceV4(selectedInvoice!.pgId);
        invoice = transformV4InvoiceToV3(v4);
      }
      const dashData = queryClient.getQueryData<{
        account: { name: string };
        current_contract?: { id: string; contract_number: string; name: string };
      }>(['account-dashboard', accountId]);
      if (dashData?.account) {
        invoice.customer = { _id: accountId, name: dashData.account.name };
      }
      if (typeof invoice.contract === 'string' && dashData?.current_contract) {
        invoice.contract = {
          _id: dashData.current_contract.id,
          contract_number: dashData.current_contract.contract_number,
          name: dashData.current_contract.name,
        };
      }
      return invoice;
    },
    enabled: !!selectedInvoice,
  });

  // Update mutation for saving changes from the modal
  const updateMutation = useMutation({
    mutationFn: ({ invoiceId, data: updateData }: { invoiceId: string; data: UpdateInvoiceInput }) =>
      updateInvoice(invoiceId, updateData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-invoices', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
      setSelectedInvoice(null);
      toast.success('Invoice updated');
    },
    onError: (error) => {
      toast.error('Failed to update invoice', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  const handleViewInvoice = (invoice: InvoiceDetail) => {
    setSelectedInvoice({
      mongoId: invoice.mongoInvoiceId || undefined,
      pgId: invoice.id,
    });
  };

  const handleInvoiceCreated = (invoice: Invoice) => {
    setSelectedInvoice({
      mongoId: invoice._id,
      pgId: invoice.pgId || invoice._id,
    });
    queryClient.invalidateQueries({ queryKey: ['account-invoices', accountId] });
    queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
  };

  const handleExportAllInvoices = async () => {
    try {
      setIsExportingAll(true);
      const allInvoices: InvoiceDetail[] = [];
      const batchSize = 100;
      let pageNum = 0;
      let totalCount = 0;

      do {
        const batch = await getAccountInvoices(accountId, {
          pageNumber: pageNum,
          pageLength: batchSize,
        });
        allInvoices.push(...(batch.data || []));
        totalCount = batch.total ?? 0;
        pageNum++;
      } while (allInvoices.length < totalCount);

      exportToCSV(allInvoices, COLUMNS, 'account-invoices-all');
      toast.success(`Exported ${allInvoices.length} invoices`);
    } catch (e) {
      console.error('Export all invoices failed:', e);
      toast.error('Failed to export all invoices');
    } finally {
      setIsExportingAll(false);
    }
  };

  // Row helpers for column renderers
  const rowHelpers: InvoiceRowHelpers = {
    onEdit: handleViewInvoice,
    onReassign: setReassignInvoice,
  };

  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-center space-y-4">
          <p className="text-destructive">Error loading invoices</p>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Invoices</CardTitle>
            <CardDescription>
              {filteredItems.length < invoices.length
                ? `Showing ${filteredItems.length} of ${invoices.length} on this page`
                : `Showing ${invoices.length} of ${total}`}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                copyToClipboard(sortedItems, COLUMNS);
                toast.success('Copied to clipboard');
              }}
            >
              <Copy className="h-4 w-4 mr-1" />
              Copy
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isExportingAll}>
                  <Download className="h-4 w-4 mr-1" />
                  {isExportingAll ? 'Exporting...' : 'CSV'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Export Invoices</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    exportToCSV(sortedItems, COLUMNS, 'account-invoices');
                    toast.success(`Exported ${sortedItems.length} rows`);
                  }}
                >
                  Export Page ({sortedItems.length} rows)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportAllInvoices}>
                  Export All Invoices ({total})
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <AccountsColumnsMenu
              orderedColumns={orderedColumns}
              isVisible={isVisible}
              onToggle={toggleColumn}
              onReorder={setColumnOrder}
            />
            {hasLegacyOrg && (
              <Button
                variant="default"
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => setIsImportOpen(true)}
              >
                <Import className="h-4 w-4 mr-1" />
                Import Invoices
              </Button>
            )}
            <Button size="sm" onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-1" />
              New Invoice
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table
            // table-layout: fixed + explicit colgroup so drag-to-resize widths
            // apply to every row (filter row, label row, body rows).
            style={{
              tableLayout: 'fixed',
              width: `${totalTableWidth}px`,
            }}
          >
            <colgroup>
              {visibleColumns.map((col) => (
                <col key={col.key} style={{ width: `${widthFor(col.key)}px` }} />
              ))}
            </colgroup>
            <TableHeader>
              {/* ROW 1: Column filters (always visible) */}
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                {visibleColumns.map((col) => renderColumnFilter(col, columnFilters, setColumnFilter))}
              </TableRow>

              {/* ROW 2: Column labels + sort + resize handles */}
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableHead
                    key={col.key}
                    className={cn(
                      'relative py-1.5 px-2',
                      col.align === 'right' && 'text-right',
                      col.align === 'center' && 'text-center',
                    )}
                  >
                    {col.sortKey ? (
                      <SortableHeader
                        label={col.label}
                        sortKey={col.sortKey}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    ) : (
                      col.label
                    )}
                    {!col.noFilter && (
                      <AccountsColumnResizeHandle
                        columnKey={col.key}
                        currentWidth={widthFor(col.key)}
                        onResize={setColumnWidth}
                      />
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {/* Loading state */}
              {isLoading && Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  {visibleColumns.map((col) => (
                    <TableCell key={col.key}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))}

              {/* Empty state */}
              {!isLoading && sortedItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} className="py-12 text-center text-muted-foreground">
                    No invoices found
                  </TableCell>
                </TableRow>
              )}

              {/* Data rows */}
              {!isLoading && sortedItems.map((invoice) => (
                <TableRow
                  key={invoice.id}
                  className="hover:bg-muted/50 cursor-pointer"
                  onClick={() => setDetailInvoice(invoice)}
                >
                  {visibleColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      className={cn(
                        // overflow-hidden + min-w-0 keep long project / manager
                        // strings from pushing the column wider than the
                        // colgroup-defined width under table-layout:fixed.
                        'text-sm py-1.5 px-2 overflow-hidden align-top min-w-0',
                        col.align === 'right' && 'text-right tabular-nums',
                        col.align === 'center' && 'text-center',
                        col.className,
                      )}
                    >
                      {col.render
                        ? col.render(invoice, rowHelpers)
                        : (String(col.getValue(invoice) ?? '') || '—')}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

      </CardContent>

      {/* Create Modal */}
      <CreateInvoiceModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onCreated={handleInvoiceCreated}
        initialCustomerId={accountId}
        initialCustomerName={dashboardData?.account?.name}
        initialCustomerAccountNumber={dashboardData?.account?.account_number}
      />

      {/* Invoice Detail Sheet — read-only view (row click) */}
      <InvoiceDetailSheet
        invoice={detailInvoice}
        open={!!detailInvoice}
        onOpenChange={(open) => { if (!open) setDetailInvoice(null); }}
      />

      {/* Invoice Form Modal — edit form (edit button) */}
      <InvoiceFormModal
        open={!!selectedInvoice}
        onOpenChange={(open) => { if (!open) setSelectedInvoice(null); }}
        invoice={invoiceLoading ? null : (fullInvoice || null)}
        onSubmit={async (updateData) => {
          if (fullInvoice) {
            await updateMutation.mutateAsync({ invoiceId: fullInvoice._id, data: updateData });
          }
        }}
        isSubmitting={updateMutation.isPending}
      />

      {/* Reassign Dialog */}
      <ReassignDialog
        entityType="invoice"
        entityId={reassignInvoice?.id ?? ''}
        entityLabel={reassignInvoice?.invoiceNumber ?? ''}
        currentAccountId={accountId}
        currentAccountName={accountName}
        open={!!reassignInvoice}
        onOpenChange={(open) => { if (!open) setReassignInvoice(null); }}
      />

      <ImportInvoicesModal
        open={isImportOpen}
        onOpenChange={setIsImportOpen}
        accountId={accountId}
        accountName={accountName}
      />
    </Card>
  );
}
